import { XeroDisabledLogo, XeroLogo } from '@approvalmax/ui';
import { Flex, Grid, Label, Radio, Tag, Text } from '@approvalmax/ui/src/components';
import { ComponentType, memo, MemoExoticComponent, useCallback, useRef } from 'react';
import { disabledClassName } from 'shared/constants';

import { accountParams } from './XeroBankAccountCard.constants';
import { Loading } from './XeroBankAccountCard.loading';
import { messages } from './XeroBankAccountCard.messages';
import { Root } from './XeroBankAccountCard.styles';
import type { ChildrenComponents, XeroBankAccountCardProps } from './XeroBankAccountCard.types';

export const XeroBankAccountCard = memo<XeroBankAccountCardProps>((props) => {
    const { id, name, disabled, matchedWithSelectedBankAccount } = props;

    const radioRef = useRef<HTMLInputElement | null>(null);

    const Logo = disabled ? XeroDisabledLogo : XeroLogo;

    const handleMouseEnter = useCallback(() => {
        radioRef.current?.focus();
    }, []);

    const handleMouseLeave = useCallback(() => {
        radioRef.current?.blur();
    }, []);

    const handleClick = useCallback(() => {
        radioRef.current?.click();
    }, []);

    return (
        <Root
            role='listitem'
            tabIndex={0}
            data-testid={`xero-bank-account-${id}`}
            // we need a class here because we need to tie to something within `has()`
            className={disabled ? disabledClassName : undefined}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        >
            <Grid gap={8}>
                <Grid
                    gap={8}
                    alignItems='center'
                    gridTemplateColumns={`repeat(2, max-content) auto${matchedWithSelectedBankAccount ? ' max-content' : ''}`}
                >
                    <Radio value={id} ref={radioRef} disabled={disabled} />

                    <Logo width={24} height={24} />

                    <Flex grow={1}>
                        {/* Flex grow=1 is for Skeleton vvv */}

                        <Text
                            font='label'
                            fontSize='medium'
                            color={disabled ? 'midnight60' : 'midnight80'}
                            ellipsis={1}
                        >
                            {name}
                        </Text>
                    </Flex>

                    {matchedWithSelectedBankAccount && (
                        <Tag color='green100' size='xsmall' title={messages.matched}>
                            {messages.matched}
                        </Tag>
                    )}
                </Grid>

                <Grid gap={8} gridTemplateColumns='48px 64px auto'>
                    {accountParams.map((prop) => (
                        <div key={prop}>
                            <Label size='xsmall'>{messages[prop]}</Label>

                            <Text font='label' fontSize='small' color={disabled ? 'midnight60' : 'midnight100'}>
                                {props[prop]}
                            </Text>
                        </div>
                    ))}
                </Grid>
            </Grid>
        </Root>
    );
}) as MemoExoticComponent<ComponentType<XeroBankAccountCardProps>> & ChildrenComponents;

XeroBankAccountCard.displayName = 'XeroBankAccountCard';

XeroBankAccountCard.Loading = Loading;
